import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const EmailGeneratorModal = ({ isOpen, onClose, contactId, contactName, contactEmail, onEmailSaved }) => {
  const [emailType, setEmailType] = useState('proposition');
  const [generatedEmail, setGeneratedEmail] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [recipient, setRecipient] = useState(contactEmail || '');
  const [sending, setSending] = useState(false);

  const emailTypes = [
    { value: 'premier_contact', label: 'Premier contact', icon: 'bi-person-plus-fill', color: 'text-blue-600' },
    { value: 'proposition', label: 'Proposition de biens', icon: 'bi-building-fill', color: 'text-purple-600' },
    { value: 'relance', label: 'Relance / Follow-up', icon: 'bi-arrow-repeat', color: 'text-orange-600' },
    { value: 'rendez_vous', label: 'Confirmation de rendez-vous', icon: 'bi-calendar-check-fill', color: 'text-green-600' },
    { value: 'documents', label: 'Demande de documents', icon: 'bi-file-earmark-text-fill', color: 'text-amber-600' },
    { value: 'personnalise', label: 'Email personnalisé libre', icon: 'bi-pencil-square', color: 'text-indigo-600' }
  ];

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setGeneratedEmail(null);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await axios.post(
        `${API_URL}/api/contacts/${contactId}/generate-email?email_type=${emailType}`
      );
      const data = response.data;
      if (data.status === 'success') {
        setGeneratedEmail(data.email);
      } else {
        setError('Erreur lors de la génération');
      }
    } catch (err) {
      setError('Erreur de connexion à l\'API');
      console.error('Error generating email:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async (saveToHistory = false) => {
    if (generatedEmail) {
      const fullEmail = `Objet: ${generatedEmail.subject}\n\n${generatedEmail.body}`;
      navigator.clipboard.writeText(fullEmail);
      
      if (saveToHistory) {
        // Sauvegarder dans l'historique
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
          await axios.post(`${API_URL}/api/contacts/${contactId}/save-email-to-history`, {
            email_type: emailType,
            subject: generatedEmail.subject,
            body: generatedEmail.body
          });
          toast.success('Email copié et enregistré dans l\'historique ✅');
          if (onEmailSaved) onEmailSaved();
        } catch (err) {
          console.error('Error saving to history:', err);
          toast.error('Email copié mais erreur lors de la sauvegarde dans l\'historique');
        }
      } else {
        toast.success('Email copié dans le presse-papier');
      }
    }
  };

  const handleSend = async () => {
    if (!generatedEmail) return;
    const to = (recipient || '').trim();
    if (!to) {
      toast.error('Veuillez renseigner l\'adresse email du destinataire');
      return;
    }
    setSending(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      await axios.post(`${API_URL}/api/email/send`, {
        to: [to],
        subject: generatedEmail.subject,
        body: generatedEmail.body,
        contact_id: contactId,
      });
      toast.success('Email envoyé ✅');
      if (onEmailSaved) onEmailSaved();
      onClose();
    } catch (err) {
      console.error('Error sending email:', err);
      const msg = err.response?.data?.detail || 'Échec de l\'envoi de l\'email';
      toast.error(typeof msg === 'string' ? msg : 'Échec de l\'envoi de l\'email');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold"><i className="bi bi-envelope-fill mr-2"></i>Rédiger email IA</h2>
              <p className="text-purple-100 text-sm mt-1">
                Pour : {contactName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-lg p-2 transition"
            >
              <i className="bi bi-x-lg text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {!generatedEmail ? (
            <>
              {/* Type selection */}
              <div className="mb-6">
                <label className="block text-sm font-bold text-slate-700 mb-3">
                  Type d'email
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {emailTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setEmailType(type.value)}
                      className={`p-4 rounded-lg border-2 transition text-left ${
                        emailType === type.value
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-slate-200 hover:border-purple-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <i className={`bi ${type.icon} text-2xl ${emailType === type.value ? 'text-purple-600' : type.color}`}></i>
                        <span className="font-semibold text-sm">{type.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Generate button */}
              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-lg font-bold text-lg shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <i className="bi bi-arrow-clockwise animate-spin mr-2"></i>
                    Génération en cours...
                  </>
                ) : (
                  <>
                    <i className="bi bi-stars mr-2"></i>
                    Générer l'email
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              {/* Generated email */}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-2">
                    DESTINATAIRE
                  </label>
                  <input
                    type="email"
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    placeholder="email@exemple.com"
                    data-testid="ai-email-recipient-input"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-2">
                    OBJET
                  </label>
                  <input
                    type="text"
                    value={generatedEmail.subject}
                    onChange={(e) =>
                      setGeneratedEmail({ ...generatedEmail, subject: e.target.value })
                    }
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-500 outline-none font-semibold"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-2">
                    CORPS DE L'EMAIL
                  </label>
                  <textarea
                    value={generatedEmail.body}
                    onChange={(e) =>
                      setGeneratedEmail({ ...generatedEmail, body: e.target.value })
                    }
                    rows="15"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-500 outline-none font-mono text-sm leading-relaxed resize-none"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 mt-6">
                <button
                  onClick={handleSend}
                  disabled={sending}
                  data-testid="ai-email-send-btn"
                  className="flex-1 min-w-[180px] py-3 bg-[#0062CC] hover:bg-[#0056b3] text-white rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <>
                      <i className="bi bi-arrow-clockwise animate-spin mr-2"></i>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send mr-2"></i>
                      Envoyer l'email
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleCopy(false)}
                  className="flex-1 min-w-[120px] py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold transition"
                >
                  <i className="bi bi-clipboard mr-2"></i>
                  Copier
                </button>
                <button
                  onClick={() => handleCopy(true)}
                  className="flex-1 min-w-[160px] py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition"
                >
                  <i className="bi bi-archive mr-2"></i>
                  Copier &amp; Historiser
                </button>
                <button
                  onClick={() => {
                    setGeneratedEmail(null);
                    setEmailType('proposition');
                  }}
                  title="Régénérer"
                  className="px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition"
                >
                  <i className="bi bi-arrow-clockwise"></i>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailGeneratorModal;
